import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faSpinner,
  faTimesCircle,
  faSyncAlt,
  faCheckCircle,
  faExclamationCircle,
  faFileAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("authentication/token") ||
    localStorage.getItem("auth_token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DOC_TYPES = [
  { id: 1,  label: "Full name of Insured with ID" },
  { id: 2,  label: "Full name of Claimant with ID" },
  { id: 3,  label: "Specimen signature or biometric information (Insured)" },
  { id: 4,  label: "Incident / Police Report" },
  { id: 5,  label: "Attending Physician's Report / Clinical Abstract" },
  { id: 6,  label: "Itemized breakdown of charges / Statement of Account" },
  { id: 7,  label: "Emergency Room Record" },
  { id: 8,  label: "Medical Certificate" },
  { id: 9,  label: "Official Receipt" },
  { id: 10, label: "Full name of Payee with ID" },
  { id: 11, label: "Payee and Insured relationship certificate (e.g. Family Card)" },
  { id: 12, label: "Specimen signature or biometric information (Payee)" },
  { id: 13, label: "Charge Slips" },
  { id: 14, label: "Operative Report" },
  { id: 15, label: "Certification of non-availability of medicines" },
];

const docLabel = (id) =>
  DOC_TYPES.find((d) => d.id === Number(id))?.label || `Type ${id}`;

const buildVersion1DocumentLink = (fileName) => {
  if (!fileName) return "";
  return `https://allaccess.net.ph/upload_doc/reimbursement/${encodeURIComponent(
    fileName
  )}`;
};

const POLLING_STATUSES = new Set(["queued", "processing"]);
const MAX_UPLOAD_FILE_SIZE_KB = 51200;
const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_KB * 1024;
const MAX_UPLOAD_FILE_SIZE_MB = MAX_UPLOAD_FILE_SIZE_KB / 1024;

const formatFileSize = (bytes = 0) => {
  const kb = bytes / 1024;
  if (kb >= 1024) return `${(kb / 1024).toFixed(2)} MB`;
  return `${kb.toFixed(1)} KB`;
};

const prettifyApiMessage = (message) =>
  message
    .replace(/The files\.(\d+) field/g, (_, index) => `File ${Number(index) + 1}`)
    .replace(/The file field/g, "The selected file")
    .replace(/51200 kilobytes/g, `${MAX_UPLOAD_FILE_SIZE_MB} MB`);

const normalizeApiMessage = (message) => {
  if (!message) return "";
  if (typeof message === "string") return prettifyApiMessage(message);
  if (Array.isArray(message)) {
    return message.map(normalizeApiMessage).filter(Boolean).join(", ");
  }
  if (typeof message === "object") {
    return Object.values(message).map(normalizeApiMessage).filter(Boolean).join(", ");
  }
  return String(message);
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  return (
    normalizeApiMessage(data?.message) ||
    normalizeApiMessage(data?.error) ||
    normalizeApiMessage(error?.message) ||
    fallbackMessage
  );
};

const RiDocumentsTab = ({ transNo, coverageCode }) => {
  const fileInputRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const pollingAttemptRef = useRef(0);
  const pollingInFlightRef = useRef(false);

  const [documents, setDocuments] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [validationState, setValidationState] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [refreshingUploadId, setRefreshingUploadId] = useState(null);
  const [deletingUploadId, setDeletingUploadId] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, [transNo]);

  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, []);

  const fetchDocuments = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/reimbursement/transactions/${transNo}/documents`,
        { headers: getAuthHeaders() }
      );
      const docs = res?.data?.data?.documents || [];
      const ups = res?.data?.data?.uploads || [];
      setDocuments(docs);
      setUploads(ups);

      // Seed validation state from existing docs
      const vs = {};
      for (const doc of docs) {
        vs[doc.tbl_id] = {
          invalid_tag: Number(doc.invalid_tag || 0),
          invalid_remarks: doc.invalid_remarks || "",
        };
      }
      setValidationState(vs);

      return { docs, uploads: ups };
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      if (!silent) {
        toast.error("Failed to load documents.");
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
    }

    const hasPendingUploads = uploads.some((u) =>
      POLLING_STATUSES.has(String(u.status || "").toLowerCase())
    );

    if (!transNo || !hasPendingUploads) {
      pollingAttemptRef.current = 0;
      return;
    }

    const attempt = pollingAttemptRef.current;
    const baseDelayMs = Math.min(15000, 3000 + attempt * 1000);
    const delayMs =
      typeof document !== "undefined" && document.hidden
        ? Math.min(30000, baseDelayMs * 2)
        : baseDelayMs;

    pollingTimerRef.current = setTimeout(async () => {
      if (pollingInFlightRef.current) return;

      pollingInFlightRef.current = true;
      try {
        const result = await fetchDocuments({ silent: true });
        const stillPending = (result?.uploads || []).some((u) =>
          POLLING_STATUSES.has(String(u.status || "").toLowerCase())
        );

        if (stillPending) {
          pollingAttemptRef.current = Math.min(
            pollingAttemptRef.current + 1,
            20
          );
        } else {
          pollingAttemptRef.current = 0;
        }
      } finally {
        pollingInFlightRef.current = false;
      }
    }, delayMs);

    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, [uploads, transNo]);

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files).map((f) => ({
      file: f,
      docType: 1,
    }));
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  const updateDocType = (index, docType) => {
    setSelectedFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, docType: Number(docType) } : f))
    );
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    const oversizedFiles = selectedFiles.filter(
      ({ file }) => file?.size > MAX_UPLOAD_FILE_SIZE_BYTES
    );

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles
        .slice(0, 3)
        .map(({ file }) => file.name)
        .join(", ");
      const remainingCount = oversizedFiles.length - 3;
      const fileSummary =
        remainingCount > 0 ? `${fileNames}, and ${remainingCount} more` : fileNames;
      const verb = oversizedFiles.length === 1 ? "exceeds" : "exceed";
      toast.error(
        `Upload failed: ${fileSummary} ${verb} the ${MAX_UPLOAD_FILE_SIZE_MB} MB file size limit.`,
        { duration: 6000 }
      );
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("trans_no", transNo);
    formData.append("coverage", coverageCode || "RI");

    selectedFiles.forEach(({ file, docType }) => {
      formData.append("files[]", file);
      formData.append("doc_types[]", docType);
    });

    try {
      await axios.post(
        `${BASE_URL}/reimbursement/transactions/${transNo}/documents/upload`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success(
        `${selectedFiles.length} file(s) queued for upload successfully.`
      );
      setSelectedFiles([]);
      fetchDocuments();
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
        return;
      }
      toast.error(
        getApiErrorMessage(error, "Upload failed. Please try again."),
        { duration: 6000 }
      );
    } finally {
      setUploading(false);
    }
  };

  const toggleInvalidTag = (tblId) => {
    setValidationState((prev) => ({
      ...prev,
      [tblId]: {
        ...prev[tblId],
        invalid_tag: prev[tblId]?.invalid_tag === 1 ? 0 : 1,
      },
    }));
  };

  const setRemarks = (tblId, remarks) => {
    setValidationState((prev) => ({
      ...prev,
      [tblId]: { ...prev[tblId], invalid_remarks: remarks },
    }));
  };

  const handleValidate = async () => {
    if (!documents.length) {
      toast.error("No documents to validate.");
      return;
    }

    const docsPayload = documents.map((doc) => ({
      tbl_id: doc.tbl_id,
      doc_type: doc.doc_type,
      invalid_tag: validationState[doc.tbl_id]?.invalid_tag ?? 0,
      invalid_remarks: validationState[doc.tbl_id]?.invalid_remarks ?? "",
    }));

    setValidating(true);
    try {
      const res = await axios.put(
        `${BASE_URL}/reimbursement/transactions/${transNo}/documents/validate`,
        { coverage: coverageCode || "RI", documents: docsPayload },
        { headers: getAuthHeaders() }
      );
      const newStatus = res?.data?.data?.trans_flag_status;
      const isInvalid = res?.data?.data?.is_invalid;
      if (isInvalid) {
        toast.error(
          "Validation complete — some documents are marked invalid (status set to 07)."
        );
      } else {
        toast.success("All documents validated successfully (status set to 10).");
      }
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(error?.response?.data?.message || "Validation failed.");
    } finally {
      setValidating(false);
    }
  };

  const handleRefreshSignedUrl = async (uploadId) => {
    if (!uploadId) return;

    setRefreshingUploadId(uploadId);
    try {
      const res = await axios.post(
        `${BASE_URL}/reimbursement/transactions/${transNo}/documents/refresh-url`,
        { upload_id: uploadId },
        { headers: getAuthHeaders() }
      );

      const signedUrl = res?.data?.signed_url || "";
      const expiresAt = res?.data?.expires_at || null;

      setUploads((prev) =>
        prev.map((u) =>
          u.upload_id === uploadId
            ? {
                ...u,
                signed_url: signedUrl || u.signed_url,
                url_expires_at: expiresAt || u.url_expires_at,
              }
            : u
        )
      );

      toast.success(res?.data?.message || "Signed URL checked.");
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
        return;
      }
      toast.error(
        error?.response?.data?.message || "Failed to refresh signed URL."
      );
    } finally {
      setRefreshingUploadId(null);
    }
  };

  const handleDeleteUpload = async (uploadId) => {
    if (!uploadId) return;

    const confirmed = window.confirm(
      "This action cannot be undone. Are you sure you want to delete this uploaded file?"
    );
    if (!confirmed) return;

    setDeletingUploadId(uploadId);
    try {
      const res = await axios.delete(
        `${BASE_URL}/reimbursement/transactions/${transNo}/documents/uploads/${uploadId}`,
        { headers: getAuthHeaders() }
      );

      setUploads((prev) => prev.filter((u) => u.upload_id !== uploadId));
      toast.success(res?.data?.message || "Uploaded file deleted successfully.");

      // Keep committed docs in sync when the deleted upload already had a committed row.
      fetchDocuments({ silent: true });
    } catch (error) {
      if (error?.response?.status === 401) {
        window.location.href = "/login";
        return;
      }
      toast.error(error?.response?.data?.message || "Failed to delete uploaded file.");
    } finally {
      setDeletingUploadId(null);
    }
  };

  const hasInvalidMark = Object.values(validationState).some(
    (v) => v.invalid_tag === 1
  );
  const hasOversizedSelectedFiles = selectedFiles.some(
    ({ file }) => file?.size > MAX_UPLOAD_FILE_SIZE_BYTES
  );

  return (
    <div className="space-y-5">
      {/* Upload Section */}
      <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Upload Documents
        </h2>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-dashed border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-100"
          >
            <FontAwesomeIcon icon={faFileAlt} />
            Select Files
          </button>
          <p className="text-xs text-gray-400">
            Accepted: Images, PDF, DOC, DOCX, XLS, XLSX, CSV. Max {MAX_UPLOAD_FILE_SIZE_MB} MB each.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-4 space-y-3">
            <p className="text-sm font-semibold text-gray-700">
              {selectedFiles.length} file(s) selected — assign a document type
              to each:
            </p>
            {hasOversizedSelectedFiles && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                One or more selected files exceed the {MAX_UPLOAD_FILE_SIZE_MB} MB upload limit. Remove or replace the highlighted file before uploading.
              </div>
            )}
            {selectedFiles.map((sf, index) => {
              const isOversized = sf.file.size > MAX_UPLOAD_FILE_SIZE_BYTES;

              return (
                <div
                  key={index}
                  className={`flex flex-col gap-2 rounded-xl border p-3 md:flex-row md:items-center ${
                    isOversized
                      ? "border-red-200 bg-red-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <span className="flex-1 truncate text-sm text-gray-800">
                    {sf.file.name}{" "}
                    <span className={`text-xs ${isOversized ? "text-red-600" : "text-gray-400"}`}>
                      ({formatFileSize(sf.file.size)})
                    </span>
                    {isOversized && (
                      <span className="ml-2 text-xs font-semibold text-red-600">
                        Exceeds {MAX_UPLOAD_FILE_SIZE_MB} MB limit
                      </span>
                    )}
                  </span>
                  <select
                    value={sf.docType}
                    onChange={(e) => updateDocType(index, e.target.value)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-orange-500 focus:outline-none md:w-64"
                  >
                    {DOC_TYPES.map((dt) => (
                      <option key={dt.id} value={dt.id}>
                        {dt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeSelectedFile(index)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} />
                  </button>
                </div>
              );
            })}

            <button
              onClick={handleUpload}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
            >
              {uploading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faUpload} />
              )}
              Upload {selectedFiles.length} File(s)
            </button>
          </div>
        )}
      </div>

      {/* Documents List + Validation */}
      <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Submitted Documents
          </h2>
          <button
            onClick={fetchDocuments}
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="text-xs" />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-56 rounded bg-gray-200" />
            <div className="overflow-hidden rounded-xl border border-gray-100">
              <div className="h-10 bg-gray-100" />
              {[...Array(5)].map((_, idx) => (
                <div key={`documents-skeleton-${idx}`} className="grid grid-cols-12 gap-3 border-t border-gray-100 px-3 py-3">
                  <div className="col-span-4 h-4 rounded bg-gray-200" />
                  <div className="col-span-3 h-4 rounded bg-gray-200" />
                  <div className="col-span-2 h-6 rounded-full bg-gray-200" />
                  <div className="col-span-3 h-4 rounded bg-gray-200" />
                </div>
              ))}
            </div>
          </div>
        ) : documents.length === 0 && uploads.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 py-10 text-center text-gray-400">
            No documents uploaded yet for this transaction.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-600">
                  Upload Queue
                </p>
                {uploads.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
                    No uploaded files in queue.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-600">
                            File Name
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-600">
                            Doc Type
                          </th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-600">
                            Status
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-600">
                            Signed URL
                          </th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-600">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 bg-white">
                        {uploads.map((u) => (
                          <tr key={u.upload_id}>
                            <td className="px-3 py-2 text-gray-800">
                              {u.original_file_name || u.file_name}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {docLabel(u.doc_type)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span
                                className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  u.status === "success"
                                    ? "bg-green-100 text-green-700"
                                    : u.status === "failed" || u.status === "error"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {u.status}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              {u.signed_url ? (
                                <a
                                  href={u.signed_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-blue-600 underline hover:text-blue-800"
                                >
                                  View File
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleRefreshSignedUrl(u.upload_id)}
                                  disabled={refreshingUploadId === u.upload_id || u.status !== "success"}
                                  className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  title={
                                    u.status === "success"
                                      ? "Check expiry and refresh signed URL if needed"
                                      : "Available after upload status is success"
                                  }
                                >
                                  {refreshingUploadId === u.upload_id ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faSyncAlt} />
                                  )}
                                  Refresh Link
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDeleteUpload(u.upload_id)}
                                  disabled={deletingUploadId === u.upload_id}
                                  className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  title="Delete uploaded file"
                                >
                                  {deletingUploadId === u.upload_id ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faTrash} />
                                  )}
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-gray-600">
                  Committed Documents (for validation)
                </p>
                {documents.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
                    No committed documents yet.
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">
                              File Name
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">
                              Doc Type
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">
                              Version1 Link
                            </th>
                            <th className="px-3 py-2 text-center font-semibold text-gray-600">
                              Mark Invalid
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">
                              Invalid Remarks
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 bg-white">
                          {documents.map((doc) => {
                            const isInvalid =
                              validationState[doc.tbl_id]?.invalid_tag === 1;
                            const version1Link = buildVersion1DocumentLink(doc.file_name);
                            return (
                              <tr key={doc.tbl_id} className={isInvalid ? "bg-red-50" : ""}>
                                <td className="px-3 py-2 font-medium text-gray-800">
                                  {doc.file_name || "—"}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {docLabel(doc.doc_type)}
                                </td>
                                <td className="px-3 py-2">
                                  {version1Link ? (
                                    <a
                                      href={version1Link}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-blue-600 underline hover:text-blue-800"
                                    >
                                      View V1 File
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    onClick={() => toggleInvalidTag(doc.tbl_id)}
                                    className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm transition-colors ${
                                      isInvalid
                                        ? "bg-red-100 text-red-600 hover:bg-red-200"
                                        : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                    }`}
                                  >
                                    <FontAwesomeIcon
                                      icon={isInvalid ? faExclamationCircle : faCheckCircle}
                                    />
                                  </button>
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    value={validationState[doc.tbl_id]?.invalid_remarks || ""}
                                    onChange={(e) => setRemarks(doc.tbl_id, e.target.value)}
                                    disabled={!isInvalid}
                                    placeholder="Reason for rejection..."
                                    className="w-full rounded border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400 focus:border-orange-400 focus:outline-none"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-3 flex items-center gap-3">
                      {hasInvalidMark && (
                        <span className="text-xs font-semibold text-red-600">
                          ⚠ {Object.values(validationState).filter((v) => v.invalid_tag === 1).length} document(s) marked invalid
                        </span>
                      )}
                      <button
                        onClick={handleValidate}
                        disabled={validating}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                      >
                        {validating ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faCheckCircle} />
                        )}
                        Submit Validation
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RiDocumentsTab;
